import React, { useState, useRef, useCallback } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const ICONS = {
  info:    <InfoOutlinedIcon style={{ color: "#007bff", fontSize: 28, marginRight: 12 }} />,
  warning: <WarningIcon      style={{ color: "#ffc107", fontSize: 28, marginRight: 12 }} />,
  danger:  <ErrorIcon        style={{ color: "#dc3545", fontSize: 28, marginRight: 12 }} />,
  success: <CheckCircleIcon  style={{ color: "#28a745", fontSize: 28, marginRight: 12 }} />,
};

// hook to manage message dialog state and provide a function to show messages
export function useMessageDialog() {
  const [state, setState] = useState({
    open: false,
    message: "",
    title: "Message",
    messageType: "info",
  });
  const resolveRef = useRef(null);

  const showMessage = useCallback(({ message, title, messageType = "info" }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, message, title: title ?? "Message", messageType });
    });
  }, []);

  const handleClose = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.();
  }, []);

  const dialogProps = { ...state, onClose: handleClose };

  return { showMessage, dialogProps };
}

// reusable message dialog component
const MessageDialog = ({
  open,
  onClose,
  title = "Message",
  children,
  message,
  messageType = "info", // "info" | "warning" | "danger" | "success"
  buttonText = "OK",
}) => (
  <Dialog open={open} onClose={onClose} aria-labelledby="message-dialog-title">
    <DialogTitle id="message-dialog-title" disableTypography style={{ backgroundColor: "#f0f0f0" }}>
      <Box display="flex" alignItems="center">
        <Box flexGrow={1} style={{ fontSize: "1.25rem", fontWeight: 500 }}>
          {title}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent style={{ paddingBottom: 24, paddingTop: 24 }}>
      <Box display="flex" alignItems="center">
        {ICONS[messageType] || null}
        <div style={{ whiteSpace: "pre-line", flex: 1 }}>{children ?? message}</div>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button
        variant="contained"
        onClick={onClose}
        style={{ background: "#0070C0", color: "#fff", lineHeight: "15px", fontSize: "15px" }}
      >
        {buttonText}
      </Button>
    </DialogActions>
  </Dialog>
);

export default MessageDialog;
